/* eslint-disable no-console */
import { useState, useEffect } from 'react';
import withTranslation from '@helpers/i18n';
import Notification from '@models/notification';
import getApiClient, { SESSION_KEY } from 'react/services/api_client';
import SubmitSatisfactionSurveyRating from './submit_satisfaction_survey_rating.graphql';
import PendingSatisfactionSurveysQuery from './pending_satisfaction_surveys.graphql';
import {
  CheckSVG,
  VeryDissatisfiedFace,
  DissatisfiedFace,
  SatisfiedFace,
  VerySatisfiedFace,
} from './faces';
import {
  FixedWrapper,
  Wrapper,
  HeaderBlock,
  SectionLabel,
  Headline,
  SubHeadline,
  ModuleName,
  OuterCard,
  InnerCard,
  RatingGroupTitle,
  EmojiRow,
  EmojiButton,
  FaceWrap,
  EmojiLabel,
  ActionsRow,
  SubmitBtn,
  SuccessWrap,
  CheckCircle,
  SuccessTitle,
  SuccessSubtitle,
} from './styles';

const EMOJI_RATINGS = {
  very_dissatisfied: 1,
  dissatisfied: 2,
  satisfied: 3,
  very_satisfied: 4,
};

const EMOJI_OPTIONS = [
  { key: 'very_dissatisfied', Face: VeryDissatisfiedFace, labelKey: '.rating_very_dissatisfied' },
  { key: 'dissatisfied', Face: DissatisfiedFace, labelKey: '.rating_dissatisfied' },
  { key: 'satisfied', Face: SatisfiedFace, labelKey: '.rating_satisfied' },
  { key: 'very_satisfied', Face: VerySatisfiedFace, labelKey: '.rating_very_satisfied' },
];

const SatisfactionMeter = ({ participationId, trainingName, revealModal, onSubmitted, t }) => {
  const [emojiKey, setEmojiKey] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayReveal, setDisplayReveal] = useState(revealModal);

  useEffect(() => {
    if (!displayReveal || !participationId) return;

    const client = getApiClient();
    try {
      const cached = client.readQuery({ query: PendingSatisfactionSurveysQuery });
      const exists = cached?.pending_satisfaction_surveys?.some((s) => s.id === participationId);

      if (!exists) {
        const current = cached?.pending_satisfaction_surveys || [];
        client.writeQuery({
          query: PendingSatisfactionSurveysQuery,
          data: {
            pending_satisfaction_surveys: [
              ...current,
              {
                __typename: 'PendingSatisfactionSurvey',
                id: participationId,
                training_name: trainingName,
              },
            ],
          },
        });
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(client.cache.extract()));
      }
    } catch {
      /* ignore */
    }
  }, [displayReveal, participationId]);

  useEffect(() => {
    if (!displayReveal) return;

    // Lock scroll
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Block interactions globally
    document.body.classList.add('satisfaction-modal-active');

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.classList.remove('satisfaction-modal-active');
    };
  }, [displayReveal]);

  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(() => {
      setDisplayReveal(false);
      if (onSubmitted) {
        onSubmitted();
      } else {
        const client = getApiClient();
        client.cache.modify({
          fields: {
            pending_satisfaction_surveys(existingRefs = [], { readField }) {
              return existingRefs.filter((ref) => readField('id', ref) !== participationId);
            },
          },
        });
        try {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(client.cache.extract()));
        } catch {
          /* ignore */
        }
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [submitted]);

  if (!displayReveal) return null;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await getApiClient().mutate({
        mutation: SubmitSatisfactionSurveyRating,
        variables: {
          input: {
            participation_id: participationId,
            satisfaction_rating: EMOJI_RATINGS[emojiKey],
          },
        },
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit satisfaction rating:', error);
      setDisplayReveal(false);
      NotificationsService.add(new Notification({ message: t('.submit_error'), type: 'alert' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FixedWrapper>
      <Wrapper data-satisfaction-modal data-purpose="satisfaction_meter_modal">
        <HeaderBlock>
          <SectionLabel>{t('.section_label')}</SectionLabel>
          <Headline>{t('.headline')}</Headline>
          <SubHeadline>
            {t('.module_subtitle_prefix')}{' '}
            <ModuleName>{trainingName || t('.module_name_placeholder')}</ModuleName>
            {t('.module_subtitle_suffix')}
          </SubHeadline>
        </HeaderBlock>

        <OuterCard>
          <InnerCard>
            {submitted ? (
              <SuccessWrap
                role="status"
                aria-live="polite"
                data-purpose="satisfaction_meter_success"
              >
                <CheckCircle>
                  <CheckSVG />
                </CheckCircle>
                <SuccessTitle>{t('.thank_you')}</SuccessTitle>
                <SuccessSubtitle>{t('.feedback_recorded')}</SuccessSubtitle>
              </SuccessWrap>
            ) : (
              <>
                <RatingGroupTitle id="rating-group-label">{t('.rating_title')}</RatingGroupTitle>
                <EmojiRow role="radiogroup" aria-labelledby="rating-group-label">
                  {EMOJI_OPTIONS.map(({ key, Face, labelKey }) => {
                    const selected = emojiKey === key;
                    return (
                      <EmojiButton
                        key={key}
                        type="button"
                        role="radio"
                        $selected={selected}
                        $hasSelection={emojiKey !== null}
                        onClick={() => setEmojiKey(key)}
                        aria-checked={selected}
                        aria-label={t(labelKey)}
                      >
                        <FaceWrap $selected={selected} $animKey={key}>
                          <Face size={40} />
                        </FaceWrap>
                        <EmojiLabel $selected={selected}>{t(labelKey)}</EmojiLabel>
                      </EmojiButton>
                    );
                  })}
                </EmojiRow>
                <ActionsRow>
                  <SubmitBtn
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading || emojiKey === null}
                    aria-disabled={isLoading || emojiKey === null}
                    data-purpose="satisfaction_meter_submit"
                  >
                    {isLoading ? t('.submitting') : t('.submit')}
                  </SubmitBtn>
                </ActionsRow>
              </>
            )}
          </InnerCard>
        </OuterCard>
      </Wrapper>
    </FixedWrapper>
  );
};

export default withTranslation('js.components.satisfaction_meter')(SatisfactionMeter);
